/**
 * Client ของกฎตรวจรับเข้า (Incoming Inspection Rule) — พูดกับ
 * `/v1/product-supplier-quality-rules` ตาม payload contract ที่ freeze ไว้ใน
 * `docs/API_CONTRACT.md` (D-086)
 *
 * หลักการเดียวกับ `lib/suppliers/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` **ไม่ใช่ "ยังไม่มีกฎ"** — สองอย่างนี้
 *   สลับกันเมื่อไรจะอันตรายเป็นพิเศษในกลุ่มนี้: "ยังไม่มีกฎ" แปลว่า **ต้องตรวจ**
 *   (D-059/D-080) หน้าจอที่อ่านผิดจะบอกผู้ใช้ว่าคู่นี้ยังไม่ถูกตั้งกฎ แล้วเขาจะสร้าง
 *   กฎทับของเดิม (ซึ่งจบที่ `409`) หรือเข้าใจว่าของที่รับเข้าจะถูกกักไว้ทั้งที่มีกฎ
 *   ยกเว้นอยู่แล้ว
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** และ **ไม่เขียนอะไรลง storage เลย**
 * - ทุกคำขอเดินทางผ่าน `apiFetch` ตัวเดียวกับ client อื่นทุกตัว header
 *   `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว **ไม่ใช่ที่นี่**
 *
 * เส้นทางที่ contract freeze ไว้มีสี่เส้น และไฟล์นี้ implement ครบทั้งสี่
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateQualityRuleBody,
  ProductSupplierQualityRule,
  QualityRuleListPage,
  UpdateQualityRuleBody,
} from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ
 */
export const QUALITY_RULES_PATH = '/v1/product-supplier-quality-rules';

/**
 * เส้นทางของกฎรายข้อ — `rule_id` เป็น **UUID surrogate** ไม่ใช่คู่ code ซึ่งเป็น
 * business key
 *
 * `encodeURIComponent` ไว้ด้วยแม้ UUID จะไม่มีอักขระที่ต้อง escape: ค่าที่เดินทางมา
 * ถึงที่นี่มาจาก payload ของเซิร์ฟเวอร์ และค่าที่อ่านมาแล้วแปะลง URL ดิบ ๆ คือวิธีที่
 * คำขอจะเปลี่ยน**เส้นทาง**ทั้งเส้นในวันที่ payload ไม่ใช่สิ่งที่คิดไว้
 */
export function qualityRulePath(ruleId: string): string {
  return `${QUALITY_RULES_PATH}/${encodeURIComponent(ruleId)}`;
}

/**
 * พารามิเตอร์ของ `GET /v1/product-supplier-quality-rules` ที่ contract รับ
 *
 * **ไม่มีพารามิเตอร์ค้นหาอิสระ** มีแต่ตัวกรองด้วย code แบบเต็มค่า ช่องค้นหาบนหน้าจอ
 * จึงกรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ใช่ยิงคำขอใหม่
 * (ดู `qualityRulesView.ts`)
 *
 * ⚠️ **ไม่มี `plant_code`** และจะไม่มีวันมี — ทรัพยากรนี้ไม่มีขอบเขตโรงงานเลย (D-086)
 */
export interface QualityRuleListQuery {
  readonly product_code?: string;
  readonly supplier_code?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

/**
 * ประกอบ query string — คีย์ที่ไม่ได้ระบุ (หรือว่างเปล่า) ไม่เดินทางไปเลย
 *
 * `cursor` ที่เป็นสตริงว่างถูกทิ้ง ไม่ใช่ส่งไปเป็นค่าว่าง: cursor ว่างไม่ใช่ "หน้าแรก"
 * สำหรับเซิร์ฟเวอร์ แต่เป็นค่าที่มันจะปฏิเสธว่าไม่ใช่ cursor ที่ตนออกให้
 *
 * code ที่เป็นช่องว่างล้วนก็ถูกทิ้งด้วยเหตุผลที่ต่างออกไป: ตัวกรองที่เป็นค่าว่างจะถูก
 * เทียบแบบตรงตัวกับรหัสจริงและได้ **หน้าว่าง** กลับมา ซึ่งอ่านเหมือน "ไม่มีกฎเลย"
 */
export function buildQualityRuleListQuery(query: QualityRuleListQuery = {}): string {
  const params = new URLSearchParams();
  const productCode = query.product_code?.trim() ?? '';
  if (productCode !== '') {
    params.append('product_code', productCode);
  }
  const supplierCode = query.supplier_code?.trim() ?? '';
  if (supplierCode !== '') {
    params.append('supplier_code', supplierCode);
  }
  if (query.limit !== undefined) {
    params.append('limit', String(query.limit));
  }
  const cursor = query.cursor?.trim() ?? '';
  if (cursor !== '') {
    params.append('cursor', cursor);
  }
  const serialized = params.toString();
  return serialized === '' ? '' : `?${serialized}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกันโดยไม่เพิ่ม
   * top-level error code ใหม่ ในกลุ่มนี้มันเป็นตัวเดียวที่แยก
   * `QUALITY_RULE_PAIR_IS_IMMUTABLE` ออกจาก `QUALITY_RULE_FIELD_IS_SERVER_OWNED`
   * และจาก `400` ธรรมดาของ payload ที่ผิดรูป — ทั้งสามมาด้วย `400` เท่ากัน และเป็น
   * ตัวเดียวที่แยก `PRODUCT_NOT_FOUND` ออกจาก `SUPPLIER_NOT_FOUND` ซึ่งมาด้วย `422`
   * เท่ากัน แต่เป็นคนละช่องที่ผู้ใช้ต้องกลับไปแก้
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * `403` ของกลุ่มนี้แปลว่า **แผนกของคุณไม่ได้ถือโมดูล `INVENTORY` ในระดับที่ขอ** ไม่ใช่
 * "โรงงานนอกขอบเขต": ทรัพยากรนี้ไม่มี `plant_id` เลย (D-086) จึงไม่มี plant scope ให้
 * ชนตั้งแต่ต้น — และเพราะมันเป็นโมดูลธุรกิจปกติ `ADMIN` ก็ถูกปฏิเสธด้วย `403`
 * เช่นกัน (D-060)
 *
 * `404` แปลว่า **กฎข้อที่คำขออ้างถึงไม่มีอยู่แล้ว** ซึ่งเกิดได้จริงจากรายการที่เปิด
 * ค้างไว้ขณะที่อีกคนแก้ข้อมูล — ไม่ใช่ข้อผิดพลาดของระบบ
 *
 * `conflict` (`409`) ต้องเป็นสถานะของตัวเอง **ไม่ใช่ `server-error`**: คู่ที่ซ้ำเป็น
 * สิ่งที่ผู้ใช้แก้เองได้ในสองวินาที (ไปแก้กฎข้อที่มีอยู่แล้ว) ส่วน "เซิร์ฟเวอร์ทำงาน
 * ไม่สำเร็จ" คือสิ่งที่เขาทำอะไรไม่ได้เลย
 *
 * `rule-violation` (`422`) คือช่องทางของ `PRODUCT_NOT_FOUND`/`SUPPLIER_NOT_FOUND` ซึ่ง
 * เป็นความผิดพลาดที่ผู้ใช้แก้เองได้เช่นกัน — พิมพ์รหัสผิดหนึ่งตัวอักษร
 */
export type QualityRuleFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type QualityRuleListResult =
  | { readonly status: 'ready'; readonly page: QualityRuleListPage }
  | QualityRuleFailure;

export type QualityRuleResult =
  | { readonly status: 'ready'; readonly rule: ProductSupplierQualityRule }
  | QualityRuleFailure;

export interface QualityRuleRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ client อื่นใช้ — header
   * `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- parsing -- */

function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * `next_cursor` ของ envelope — `undefined` แปลว่า "ผิดรูป" ไม่ใช่ "ไม่มีหน้าถัดไป"
 *
 * cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่างจะวน
 * ขอหน้าเดิมซ้ำไปเรื่อย ๆ
 */
function parseCursor(raw: unknown): string | null | undefined {
  const cursor = nullableString(raw);
  if (cursor === undefined) {
    return undefined;
  }
  return cursor === null || cursor.trim() === '' ? null : cursor;
}

/**
 * กฎหนึ่งข้อ — **บังคับครบทุกฟิลด์ที่ contract บังคับ**
 *
 * `incoming_inspection_required` ต้องเป็น `boolean` จริง ๆ เท่านั้น: `"true"` ที่เป็น
 * สตริง หรือ `1` ที่เป็นตัวเลข ถูกปฏิเสธเป็น `invalid-response` แทนที่จะถูก coerce —
 * ค่าที่ถูก coerce ผิดในกลุ่มนี้แปลว่าหน้าจอบอกผู้ใช้ตรงข้ามกับสิ่งที่ระบบจะทำจริง
 * ตอนของมาถึงท่ารับ
 *
 * `note` เป็น `null` ได้ (ไม่มีหมายเหตุ) แต่ชนิดที่ผิดทำให้ทั้งแถวผิดรูป
 */
export function parseQualityRule(raw: unknown): ProductSupplierQualityRule | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const ruleId = requiredString(record.rule_id);
  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const supplierId = requiredString(record.supplier_id);
  const supplierCode = requiredString(record.supplier_code);
  const createdBy = requiredString(record.created_by_employee_id);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  const note = nullableString(record.note);

  if (
    ruleId === null ||
    productId === null ||
    productCode === null ||
    supplierId === null ||
    supplierCode === null ||
    createdBy === null ||
    createdAt === null ||
    updatedAt === null ||
    note === undefined ||
    typeof record.incoming_inspection_required !== 'boolean'
  ) {
    return null;
  }

  return {
    rule_id: ruleId,
    product_id: productId,
    product_code: productCode,
    supplier_id: supplierId,
    supplier_code: supplierCode,
    incoming_inspection_required: record.incoming_inspection_required,
    note,
    created_by_employee_id: createdBy,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการกฎจาก envelope `{ items, next_cursor }` — **และจากที่นั่นเท่านั้น**
 *
 * contract freeze รูปนี้ไว้ตรง ๆ จึงไม่รับ array เปล่าเป็นทางถอย — parser ที่เขียน
 * ขึ้นจากรูปที่เดาเอาเองคือ defect ที่ทำให้หน้าจอ BOM เคยขึ้นว่า "ยังไม่มีสูตร" ให้
 * สินค้าที่มีสูตรอยู่แล้ว
 *
 * แถวเดียวที่อ่านไม่ออกทำให้ **ทั้งหน้าผิดรูป** ไม่ใช่ถูกตัดทิ้งเงียบ ๆ: รายการที่ขาด
 * ไปหนึ่งแถวคือหน้าจอที่บอกผู้ใช้ว่าคู่นั้นยังไม่มีกฎ
 */
export function parseQualityRuleListPage(raw: unknown): QualityRuleListPage | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.items)) {
    return null;
  }

  const items: ProductSupplierQualityRule[] = [];
  for (const entry of record.items) {
    const parsed = parseQualityRule(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = parseCursor(record.next_cursor);
  return cursor === undefined ? null : { items, next_cursor: cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(details.rule),
    details,
  };
}

/* ---------------------------------------------------------- serializing -- */

/**
 * แปลง body ของ `POST` ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * เขียนคีย์ที่ออกไปทีละตัวแทนการ spread state ของฟอร์ม: `extra = "forbid"` ทำให้คีย์
 * แปลกปลอมที่ติดมา (เช่นแถวของกฎที่ถูก spread ทั้งก้อนพร้อม `rule_id`) กลายเป็น
 * `400 QUALITY_RULE_FIELD_IS_SERVER_OWNED` ทั้งที่ผู้ใช้กรอกถูกทุกช่อง — และเป็นด่าน
 * ที่ทำให้ฟิลด์ของเซิร์ฟเวอร์ไปถึงเซิร์ฟเวอร์ **ไม่ได้เลย** ต่อให้ผู้เรียกถือ object
 * ที่มีคีย์นั้นอยู่ในมือ
 *
 * `note` หายไปทั้งคีย์เมื่อไม่ได้ระบุ ไม่ใช่ส่งสตริงว่าง
 */
export function serializeCreateQualityRuleBody(
  body: CreateQualityRuleBody,
): Record<string, unknown> {
  return {
    product_code: body.product_code,
    supplier_code: body.supplier_code,
    incoming_inspection_required: body.incoming_inspection_required,
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

/**
 * แปลง body ของ `PATCH` — **คู่ code ออกไปไม่ได้**
 *
 * ด่านสุดท้ายของกฎที่ `UpdateQualityRuleBody` ประกาศไว้ด้วยชนิดข้อมูล: คีย์ที่ไม่ได้
 * ระบุจะไม่เดินทางไปเลย ("ปล่อยไว้ตามเดิม") และ `product_code`/`supplier_code` ไม่มี
 * ทางหลุดไปกับคำขอ ซึ่งจะถูกตอบ `400 QUALITY_RULE_PAIR_IS_IMMUTABLE` ทั้งที่ผู้ใช้
 * แค่ปิดการตรวจ
 *
 * `note: null` **เดินทางไปจริง** ต่างจาก `note: undefined` — อันแรกคือคำสั่งล้าง
 * หมายเหตุ อันหลังคือ "ไม่แตะหมายเหตุ"
 */
export function serializeUpdateQualityRuleBody(
  body: UpdateQualityRuleBody,
): Record<string, unknown> {
  return {
    ...(body.incoming_inspection_required === undefined
      ? {}
      : { incoming_inspection_required: body.incoming_inspection_required }),
    ...(body.note === undefined ? {} : { note: body.note }),
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

type PayloadResult =
  | { readonly status: 'payload'; readonly payload: unknown }
  | QualityRuleFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: QualityRuleRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/**
 * `GET /v1/product-supplier-quality-rules` — กฎทั้งองค์กร เรียงตาม `product_code`
 * แล้ว `supplier_code`
 *
 * **ไม่เรียงลำดับซ้ำเอง** contract ระบุว่าเซิร์ฟเวอร์เป็นผู้เรียง การเรียงซ้ำที่
 * frontend จะกลบกรณีที่เซิร์ฟเวอร์เรียงผิดจนไม่มีใครเห็น
 *
 * code ที่ไม่มีกฎใดอ้างถึง = **หน้าว่าง ไม่ใช่ error** ต่างจากฝั่งเขียนโดยตั้งใจของ
 * contract: การถามว่า "มีกฎอะไรบ้างสำหรับสิ่งนี้" มีคำตอบว่า "ไม่มี" อยู่แล้ว
 */
export async function fetchQualityRules(
  query: QualityRuleListQuery = {},
  options: QualityRuleRequestOptions = {},
): Promise<QualityRuleListResult> {
  const result = await requestPayload(
    `${QUALITY_RULES_PATH}${buildQualityRuleListQuery(query)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseQualityRuleListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `GET /v1/product-supplier-quality-rules/{rule_id}` — กฎหนึ่งข้อ; ไม่มี → `404`
 *
 * มีอยู่เพราะ contract มีสี่เส้นและนี่คือเส้นที่ตอบคำถาม "กฎข้อนี้ตอนนี้เป็นอย่างไร
 * จริง ๆ" โดยไม่ต้องดึงทั้งหน้ามาใหม่ — คำตอบของมันคือแหล่งความจริงหลังการแก้ไข
 * ไม่ใช่สิ่งที่หน้าจอเพิ่งส่งไป
 */
export async function fetchQualityRule(
  ruleId: string,
  options: QualityRuleRequestOptions = {},
): Promise<QualityRuleResult> {
  const result = await requestPayload(
    qualityRulePath(ruleId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const rule = parseQualityRule(result.payload);
  return rule === null ? { status: 'invalid-response' } : { status: 'ready', rule };
}

/**
 * `POST /v1/product-supplier-quality-rules` — ตั้งกฎใหม่ ตอบ `201`
 *
 * **ไม่มี `Idempotency-Key`**: contract สงวน header นั้นไว้ให้ Scan, Goods Receipt,
 * Initial Import และ Offline Sync ที่กระทบ stock/บัญชี การตั้งกฎไม่เคลื่อนสต๊อกหรือ
 * บัญชี และคู่ที่ซ้ำถูกกันด้วย unique constraint อยู่แล้ว (`409 QUALITY_RULE_EXISTS`
 * โดยไม่เขียนอะไรเลย) การเติมคีย์ที่ endpoint ไม่ได้ขอเข้าไปคือ header ที่ไม่มีใคร
 * อ่าน ซึ่งจะสอนคนอ่านโค้ดผิดว่า endpoint นี้ต้องการมัน
 */
export async function createQualityRule(
  body: CreateQualityRuleBody,
  options: QualityRuleRequestOptions = {},
): Promise<QualityRuleResult> {
  const result = await requestPayload(
    QUALITY_RULES_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateQualityRuleBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const rule = parseQualityRule(result.payload);
  return rule === null ? { status: 'invalid-response' } : { status: 'ready', rule };
}

/**
 * `PATCH /v1/product-supplier-quality-rules/{rule_id}` — แก้ธงการตรวจ และ/หรือ หมายเหตุ
 *
 * เซิร์ฟเวอร์เขียน `audit_log` (entity_type `ProductSupplierQualityRule`) ในธุรกรรม
 * เดียวกับการแก้ข้อมูล — ผู้เรียกจึงอ่านสถานะใหม่จาก **คำตอบ** ไม่ใช่จากสิ่งที่ตัวเอง
 * เพิ่งส่งไป และนี่คือเส้นทางเดียวที่ปิดการตรวจได้ (ไม่มี `DELETE` ในกลุ่มนี้)
 */
export async function updateQualityRule(
  ruleId: string,
  body: UpdateQualityRuleBody,
  options: QualityRuleRequestOptions = {},
): Promise<QualityRuleResult> {
  const result = await requestPayload(
    qualityRulePath(ruleId),
    {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeUpdateQualityRuleBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const rule = parseQualityRule(result.payload);
  return rule === null ? { status: 'invalid-response' } : { status: 'ready', rule };
}

/**
 * อ่าน `details.rule` จากความล้มเหลวที่มี envelope — คืน `null` เมื่อไม่มี
 *
 * ไม่เดาค่าเมื่ออ่านไม่ได้ เพราะกฎที่เดาผิดจะส่งผู้ใช้ไปแก้เรื่องที่ไม่ได้เสีย —
 * โดยเฉพาะคู่ `PRODUCT_NOT_FOUND`/`SUPPLIER_NOT_FOUND` ซึ่งมาด้วย `422` เท่ากันแต่
 * ชี้ไปคนละช่องบนฟอร์ม
 */
export function ruleFromFailure(failure: QualityRuleFailure): string | null {
  return 'error' in failure ? failure.error.rule : null;
}
